import React, {FC} from 'react';

import {ActionCreatorWithPayload} from '@reduxjs/toolkit';
import {connect} from 'react-redux';

import {CODE_FILE_UNDEFINED_ERROR} from 'constants/file/fileCode';
import {JPG} from 'constants/file/fileTypes';
import {PRESCRIPTION_METHOD} from 'constants/product';
import {ApplicationStoreType} from 'desktop/store';

import {FieldType} from 'types/successPage';

import {changeField, setErrorsSuccessPage} from '../../store/actions/orderItems/orderItems';
import {getSuccessPageItems} from '../../store/selectors/orderItems/orderItems';
import {DoctorDetailsAndUploadValidatedType} from '../../types/orderItemsTypes';

import {DrDetailsRedesignConnected} from '../drDetailsRedesign/drDetailsRedesign';
import {UploadSuccessPageFileRedesignConnected} from '../uploadSuccessPageFileRedesign/uploadSuccessPageFileRedesign';

import s from './drDetailsOrUploadFileRedesignItems.scss';

type PropsType = {
    items: Array<FieldType>;
    changeSuccessPageFields: ActionCreatorWithPayload<FieldType, string>;
    isModalOpen: boolean;
    setErrors: ActionCreatorWithPayload<DoctorDetailsAndUploadValidatedType, string>;
};

/**
 * Method to get page title and description depending on items sendBy properties
 * @param {Array<FieldType>} items - order items array
 * @returns {React.ReactElement} Rendered react component
 */
// ts-unused-exports:disable-next-line
export const CardsDescription = React.memo(
    ({items}: {items: Array<FieldType>}) => {
        const isOnlyUploadFileCards = items.every(
            item => item.sendBy === PRESCRIPTION_METHOD.SEND_EMAIL_OR_FAX_METHOD,
        );
        const isOnlyDrDetailsCards = items.every(
            item => item.sendBy !== PRESCRIPTION_METHOD.SEND_EMAIL_OR_FAX_METHOD,
        );

        if (!isOnlyUploadFileCards && !isOnlyDrDetailsCards) {
            return (
                <>
                    <h3 className={s.title}>
                        <b>Wait!</b> We need your doctor&apos;s details.
                    </h3>
                    <p className={s.description}>
                        Fill them in below (it&apos;s required by law) &<br />
                        upload your prescription. We can&apos;t send you
                        <br /> your contacts without them.
                    </p>
                </>
            );
        }

        if (isOnlyUploadFileCards) {
            return (
                <>
                    <h3 className={s.title}>
                        <b>Last step,</b>
                        upload your prescription.
                    </h3>
                    <p className={s.description}>
                        We can&apos;t send you your contacts without it.
                    </p>
                </>
            );
        }

        return (
            <>
                <h3 className={s.title}>
                    <b>Wait!</b> We need your doctor&apos;s details.
                </h3>
                <p className={s.description}>
                    We just need one more detail, required by law
                    <br />
                    so we can send your contacts.
                </p>
            </>
        );
    },
    ({items: prevItems}, {items: nextItems}) => prevItems.length === nextItems.length,
);

CardsDescription.displayName = 'CardsDescription';
/**
 * This component has dr details or upload file, it depends on sendBy
 * @param {PropsType} props - props type
 * @returns {React.ReactElement} Rendered react component
 */
const DrDetailsOrUploadFileRedesignItems: FC<PropsType> = ({
    changeSuccessPageFields,
    isModalOpen,
    items,
    setErrors,
}) => (
    <>
        <CardsDescription items={items} />
        {items.map(
            (
                {
                    doctorDetails,
                    errorType = CODE_FILE_UNDEFINED_ERROR,
                    fileName = '',
                    fileSrc = '',
                    fileType = JPG,
                    image,
                    name,
                    orderItemId,
                    sendBy,
                    type = '',
                },
                i,
            ) =>
                sendBy === PRESCRIPTION_METHOD.SEND_EMAIL_OR_FAX_METHOD ? (
                    <UploadSuccessPageFileRedesignConnected
                        changeField={changeSuccessPageFields}
                        error={errorType}
                        fileName={fileName}
                        fileSrc={fileSrc}
                        fileType={fileType}
                        image={image}
                        key={orderItemId}
                        name={name}
                        orderItemId={orderItemId}
                        setErrors={setErrors}
                        type={type}
                    />
                ) : (
                    <DrDetailsRedesignConnected
                        changeField={changeSuccessPageFields}
                        doctorDetails={doctorDetails}
                        image={image}
                        isModalOpen={i === 0 ? isModalOpen : null}
                        key={orderItemId}
                        name={name}
                        orderItemId={orderItemId}
                        setErrors={setErrors}
                    />
                ),
        )}
    </>
);

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType) => ({
    items: getSuccessPageItems(store),
});

export const DrDetailsOrUploadFileRedesignItemsConnected = connect(mapStateToProps, {
    changeSuccessPageFields: changeField,
    setErrors: setErrorsSuccessPage,
})(DrDetailsOrUploadFileRedesignItems);
